import React from 'react';
import {
  Box,
  Typography,
  Paper,
  useTheme,
  useMediaQuery,
  IconButton
} from '@mui/material';
import {
  CurrencyExchange as CurrencyExchangeIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';

const CompactExchangeRateDisplay = ({ 
  exchangeRate, 
  loading = false, 
  error = null, 
  onClick 
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  if (loading) {
    return (
      <Paper 
        elevation={0}
        sx={{ 
          background: 'linear-gradient(135deg, #f8f9ff 0%, #f0f4ff 100%)',
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          p: 2,
          cursor: 'pointer',
          transition: 'all 0.3s ease-in-out',
          '&:hover': { 
            borderColor: theme.palette.primary.main,
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 24px rgba(102, 126, 234, 0.15)'
          }
        }}
        onClick={onClick}
      >
        <Box display="flex" alignItems="center" justifyContent="center">
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
            Loading exchange rates...
          </Typography>
        </Box>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper 
        elevation={0}
        sx={{ 
          background: 'linear-gradient(135deg, #fff2f2 0%, #ffebee 100%)',
          border: `1px solid ${theme.palette.error.light}`,
          borderRadius: 2,
          p: 2,
          cursor: 'pointer',
          transition: 'all 0.3s ease-in-out',
          '&:hover': { 
            borderColor: theme.palette.error.main,
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 24px rgba(244, 67, 54, 0.15)'
          }
        }}
        onClick={onClick}
      >
        <Box display="flex" alignItems="center" justifyContent="center">
          <Typography variant="body2" color="error" sx={{ fontWeight: 500 }}>
            Exchange rates unavailable - Click to retry
          </Typography>
        </Box>
      </Paper>
    );
  }

  if (!exchangeRate) {
    return (
      <Paper 
        elevation={0}
        sx={{ 
          background: 'linear-gradient(135deg, #f8f9ff 0%, #f0f4ff 100%)',
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          p: 2,
          cursor: 'pointer',
          transition: 'all 0.3s ease-in-out',
          '&:hover': { 
            borderColor: theme.palette.primary.main,
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 24px rgba(102, 126, 234, 0.15)'
          }
        }}
        onClick={onClick}
      >
        <Box display="flex" alignItems="center" justifyContent="center">
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
            No exchange rate data
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper 
      elevation={0}
      sx={{ 
        background: 'linear-gradient(135deg, #f8f9ff 0%, #f0f4ff 100%)',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        p: 2.5,
        mb: 3,
        cursor: 'pointer',
        transition: 'all 0.3s ease-in-out',
        position: 'relative',
        overflow: 'hidden',
        '&:hover': { 
          borderColor: theme.palette.primary.main,
          transform: 'translateY(-2px)',
          boxShadow: '0 12px 32px rgba(102, 126, 234, 0.2)',
          '& .exchange-icon': {
            transform: 'scale(1.1) rotate(5deg)'
          }
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
          opacity: exchangeRate.validflag ? 1 : 0.3
        }
      }}
      onClick={onClick}
    >
      <Box display="flex" alignItems="center" justifyContent={isMobile ? "center" : "space-between"}>
        {!isMobile && (
          <Box display="flex" alignItems="center">
            <IconButton 
              size="small" 
              sx={{ 
                backgroundColor: 'rgba(102, 126, 234, 0.1)',
                color: theme.palette.primary.main,
                mr: 1.5,
                transition: 'all 0.3s ease-in-out'
              }}
              className="exchange-icon"
            >
              <CurrencyExchangeIcon sx={{ fontSize: 18, color: theme.palette.secondary.main }} />
            </IconButton>
            <Box>
              <Typography 
                variant="body2" 
                sx={{ 
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  fontSize: '0.85rem',
                  mb: 0.2
                }}
              >
                Current Exchange Rates
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: theme.palette.text.secondary,
                  fontSize: '0.7rem'
                }}
              >
                Click for detailed information
              </Typography>
            </Box>
          </Box>
        )}
        
        <Box display="flex" alignItems="center" gap={isMobile ? 2 : 3}>
          {/* USD Rate */}
          <Box textAlign="right">
            <Typography 
              variant="body2" 
              sx={{ 
                fontWeight: 600,
                fontSize: '0.85rem',
                color: theme.palette.text.primary,
                lineHeight: 1.2
              }}
            >
              {exchangeRate.USD ? `$1 USD = ฿${exchangeRate.USD.toFixed(2)} THB` : '$1 USD = N/A THB'}
            </Typography>
          </Box>         
    
          
          {/* LAK Rate */}
          <Box textAlign="right">
            <Typography 
              variant="body2" 
              sx={{ 
                fontWeight: 600,
                fontSize: '0.85rem',
                color: theme.palette.text.primary,
                lineHeight: 1.2
              }}
            >
              {exchangeRate.LAK ? `₭${exchangeRate.LAK.toFixed(0)} LAK = ฿1 THB` : '₭N/A LAK = ฿1 THB'}
            </Typography>
          </Box>

          {/* Status Indicator */}
          <Box display="flex" alignItems="center" ml={1}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: exchangeRate.validflag ? '#4caf50' : '#ff9800',
                boxShadow: exchangeRate.validflag ? '0 0 8px rgba(76, 175, 80, 0.5)' : '0 0 8px rgba(255, 152, 0, 0.5)',
                animation: exchangeRate.validflag ? 'pulse 2s infinite' : 'none'
              }}
            />
            <TrendingUpIcon 
              sx={{ 
                ml: 0.5,
                fontSize: 16, 
                color: theme.palette.action.active,
                opacity: 0.7
              }} 
            />
          </Box>
        </Box>
      </Box>

      <style>
        {`
          @keyframes pulse {
            0% { opacity: 1; }
            50% { opacity: 0.5; }
            100% { opacity: 1; }
          }
        `}
      </style>
    </Paper>
  );
};

export default CompactExchangeRateDisplay;